import React from 'react';
import ReportShell, { reportStyle, reportButtonStyle, formatReportTimestamp } from '../../../shared/ReportShell';
import { downloadCsv } from '../../../shared/csv';
import { formatDate } from '../../../shared/tenantClock';
import { formatMoney as formatMoneyShared, formatNumber, getTenantHomeCurrency } from '../../../shared/numberFormat';

// '—' for blank; `currency` marks the Supplier's foreign cost.
function formatMoney(value, currency) {
  return formatMoneyShared(value, { currency }) || '—';
}

function formatRate(value) {
  return formatNumber(value, { decimals: 6 }) || '—';
}

// One row per cost-schedule entry (current and historical alike) rather
// than one row per Product — this report exists specifically to answer
// "what has this SKU's Cost Price been over time", which a one-row-per-
// product summary (mirroring ProductSummaryReportView.js) can't show.
// Products with no schedule entries at all are omitted rather than shown
// as a blank row, since there's no history to report. Each entry converts
// at its own exchange rate (issue #303).
function buildHistoryRows(products) {
  const rows = [];
  products.forEach((product) => {
    const costFieldName = product.supplier ? 'cost_price_foreign' : 'cost_price';
    const entries = [...(product.cost_schedule || [])].sort((a, b) => a.effective_from.localeCompare(b.effective_from));
    entries.forEach((entry) => {
      const cost = entry[costFieldName];
      // A home-currency Supplier's cost needs no rate.
      const isHome = product.supplier?.currency_code === getTenantHomeCurrency();
      const exchangeRate = isHome ? null : (entry.exchange_rate ?? null);
      const rate = isHome ? 1 : exchangeRate;
      const aud = costFieldName === 'cost_price_foreign' && cost !== null && rate !== null
        ? Number(cost) * Number(rate)
        : null;
      rows.push({
        sku: product.sku,
        name: product.name,
        effectiveFrom: entry.effective_from,
        effectiveTo: entry.effective_to,
        cost,
        exchangeRate: costFieldName === 'cost_price_foreign' ? exchangeRate : null,
        aud,
      });
    });
  });
  return rows;
}

function buildCsvRows(historyRows, currencyCode) {
  return historyRows.map((row) => ({
    SKU: row.sku,
    Name: row.name,
    'Effective From': row.effectiveFrom,
    'Effective To': row.effectiveTo || 'open-ended',
    [`Cost (${currencyCode})`]: row.cost !== null && row.cost !== undefined ? Number(row.cost).toFixed(2) : '',
    'Exchange Rate': row.exchangeRate !== null && row.exchangeRate !== undefined ? Number(row.exchangeRate).toFixed(6) : '',
    'Cost (AUD)': row.aud !== null && row.aud !== undefined ? Number(row.aud).toFixed(2) : '',
  }));
}

// Contextual report (pat-architecture-reports-ui-standard.md) for the
// Supplier-scoped Cost Price Schedule screen — mirrors whatever's currently
// filtered there, fetched unpaginated (`all: true`) by the caller so the
// report covers every matching Product, not just the on-screen page.
function CostPriceHistoryReportView({ products, loading, filterSummary, currencyCode, onClose }) {
  const historyRows = loading ? [] : buildHistoryRows(products);
  const isEmpty = !loading && historyRows.length === 0;
  const productCount = products.length;

  return (
    <ReportShell
      title="Cost Price History Report"
      subtitle={(
        <>
          <div>{`${formatReportTimestamp()} · ${productCount} product${productCount === 1 ? '' : 's'} · ${historyRows.length} entr${historyRows.length === 1 ? 'y' : 'ies'}`}</div>
          {filterSummary && <div>Filtered by: {filterSummary}</div>}
        </>
      )}
      onClose={onClose}
      printDisabled={loading || isEmpty}
      actions={(
        <button
          type="button"
          onClick={() => downloadCsv('cost-price-history-report.csv', buildCsvRows(historyRows, currencyCode))}
          disabled={loading || isEmpty}
          style={reportButtonStyle(loading || isEmpty)}
        >
          Download CSV
        </button>
      )}
    >
      {loading ? (
        <div style={{ padding: 24, textAlign: 'center', color: '#888' }}>Loading...</div>
      ) : isEmpty ? (
        <div style={{ padding: 24, textAlign: 'center', color: '#888' }}>No Cost Price history matches the current filters.</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: reportStyle.tableFontSize }}>
          <thead>
            <tr className="print-only-row"><td colSpan={7} style={{ height: 18, padding: 0, border: 'none' }} /></tr>
            <tr style={{ textAlign: 'left', borderBottom: reportStyle.tableHeaderBorder }}>
              <th style={{ padding: '6px 8px 6px 0' }}>SKU</th>
              <th style={{ padding: '6px 8px 6px 0' }}>Name</th>
              <th style={{ padding: '6px 8px 6px 0' }}>Effective From</th>
              <th style={{ padding: '6px 8px 6px 0' }}>Effective To</th>
              <th style={{ padding: '6px 8px 6px 0', textAlign: 'right', whiteSpace: 'nowrap' }}>Cost ({currencyCode})</th>
              <th style={{ padding: '6px 8px 6px 0', textAlign: 'right', whiteSpace: 'nowrap' }}>Exchange Rate</th>
              <th style={{ padding: '6px 8px 6px 0', textAlign: 'right', whiteSpace: 'nowrap' }}>{getTenantHomeCurrency()}</th>
            </tr>
          </thead>
          <tfoot>
            <tr className="print-only-row"><td colSpan={7} style={{ height: 20, padding: 0, border: 'none' }} /></tr>
          </tfoot>
          <tbody>
            {historyRows.map((row, idx) => (
              <tr key={`${row.sku}-${row.effectiveFrom}-${idx}`} style={{ borderBottom: '1px solid #ddd', breakInside: 'avoid', pageBreakInside: 'avoid' }}>
                <td style={{ padding: '6px 8px 6px 0', fontWeight: 600 }}>{row.sku}</td>
                <td style={{ padding: '6px 8px 6px 0' }}>{row.name}</td>
                <td style={{ padding: '6px 8px 6px 0', whiteSpace: 'nowrap' }}>{formatDate(row.effectiveFrom)}</td>
                <td style={{ padding: '6px 8px 6px 0', whiteSpace: 'nowrap' }}>{formatDate(row.effectiveTo) || 'open-ended'}</td>
                <td style={{ padding: '6px 8px 6px 0', textAlign: 'right' }}>{formatMoney(row.cost, currencyCode)}</td>
                <td style={{ padding: '6px 8px 6px 0', textAlign: 'right', color: '#888' }}>{row.exchangeRate !== null ? formatRate(row.exchangeRate) : '—'}</td>
                <td style={{ padding: '6px 8px 6px 0', textAlign: 'right', color: '#888' }}>{formatMoney(row.aud)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </ReportShell>
  );
}

export default CostPriceHistoryReportView;
